import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { UserDto } from "../../../models/user/dto/user.dto";

export type ActivityFilter = {
    activeAfter?: string;
    activeBefore?: string;
    inactive?: boolean;
    cohortId?: string;
};

export type UseGetUsersOptions = {
    /**
     * Gate for consumers that MOUNT alongside a page but only need the roster
     * once something is actually opened (Sentry DASHBOARD-Z).
     *
     * `GET /users` is the largest read the dashboard makes — the whole
     * organization roster, up to 5,000 fully-serialized users plus their
     * `contactInfo`, with no pagination — which is why Sentry's
     * large-HTTP-payload detector files it as DASHBOARD-Z (144 users,
     * 11,062 occurrences). Four of the five call sites are pickers inside
     * CLOSED modals that are rendered next to a page rather than on open, so
     * merely visiting a game / coupon / cohort / member page pulled the entire
     * roster for a dialog nobody had opened. React Query's defaults make that
     * worse than once: this app configures no `staleTime`, so the fetch repeats
     * on every remount and every window focus.
     *
     * Defaults to `true` so the roster page itself (`UserList`) is unchanged.
     */
    enabled?: boolean;
};

export const useGetUsers = (
    organizationId: string,
    activityFilter?: ActivityFilter,
    options?: UseGetUsersOptions,
) => {
    const client = useApiClient();

    const params = new URLSearchParams();
    params.set('organizationId', organizationId || '');
    if (activityFilter?.activeAfter) params.set('activeAfter', activityFilter.activeAfter);
    if (activityFilter?.activeBefore) params.set('activeBefore', activityFilter.activeBefore);
    if (activityFilter?.inactive) params.set('inactive', 'true');
    if (activityFilter?.cohortId) params.set('cohortId', activityFilter.cohortId);

    return useQuery({
        enabled: Boolean(organizationId) && (options?.enabled ?? true),
        queryKey: [organizationId, 'users', activityFilter],
        queryFn: () => client.fetchApi<UserDto[], {}>({ endpoint: `users?${params.toString()}`, method: 'GET' }),
        // No per-user cache priming here — deliberately.
        //
        // This hook used to `setQueryData(GET_USER_BY_ID_QUERY_KEY(id), user)`
        // for every row it received. Two things were wrong with that:
        //
        // 1. It was the dashboard's org-switch freeze. React Query v4's
        //    `setQueryData` runs `queryCache.find`, a LINEAR scan that hashes
        //    every cached key, so priming N users costs O(N × cache size) — and
        //    the cache GROWS by N on every roster load, because the per-user
        //    keys are not org-scoped and live their 5-minute `cacheTime`. A
        //    platform admin switching between organizations on the users page
        //    paid 4,000 × (4,000 + everything visited before) key hashes per
        //    switch: 2.5 s of main-thread time for one 4,000-user org in a CPU
        //    profile, more on each further switch, until the tab was declared
        //    unresponsive. The rendering half of that freeze is fixed in
        //    `UserList` (pagination).
        // 2. It wrote the wrong TYPE into the slot. `GET_USER_BY_ID_QUERY_KEY`
        //    holds `UserFullProfileDto` from `GET users/:id/details`; the roster
        //    row is a `UserDto` without the profile fields, so `UserPage`
        //    briefly rendered a profile with those fields missing until its own
        //    fetch (no `staleTime`, so it always refetched anyway) replaced it.
        //
        // `useGetUser` fetches its own row on demand; nothing relied on the prime.
    });
};
